import toast from 'react-hot-toast';
import { BACKEND_URL } from '@/utils/endpoints';
import { getFirebaseAuthHeaders } from '@/utils/functions/auth';
import * as Sentry from '@sentry/react';
/* PUT FUNCTIONS - APPLY AND BOOKMARK JOB */

export const applyToJob = async ({ uid, jobID }) => {
  try {
    const response = await fetch(
        `${BACKEND_URL}/api/jobs/apply/${uid}/${jobID}`,
      {
        method: 'PUT',
        headers: await getFirebaseAuthHeaders(undefined, {
          'Content-Type': 'application/json',
        }),
      },
    );

    if (response.ok) {
      toast.success('Successfully marked the opportunity as applied!');
      return { success: true };
    }
    toast.error('Failed to apply to the job.');
    return { success: false };
  } catch (error) {
    Sentry.captureException(error);
    toast.error('An error occurred while applying to the job.');
    console.error('Error:', error);
    return { success: false };
  }
};

export const bookmarkJob = async ({ uid, jobData }) => {
  try {
    const { id, ...jobDataWithoutId } = jobData; // Exclude the 'id' key from jobData

    const response = await fetch(
        `${BACKEND_URL}/api/jobs/bookmark-job/${uid}/${id}`,
      {
        method: 'POST',
        headers: await getFirebaseAuthHeaders(undefined, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ data: jobDataWithoutId }),
      },
    );

    if (response.ok) {
      toast.success('Successfully bookmarked the job!');
      return { success: true };
    }
    toast.error('Failed to bookmark the job.');
    return { success: false };
  } catch (error) {
    Sentry.captureException(error);
    toast.error('An error occurred while bookmarking the job.');
    console.error('Error:', error);
    return { success: false };
  }
};

/* GET FUNCTIONS  */

export const fetchJobs = async () => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/jobs/get-all-opportunities`,
    );
    const data = await response.json();
    const jobsArray = Object.keys(data).map((key) => ({
      id: key,
      ...data[key],
    }));
    return jobsArray;
  } catch (error) {
    Sentry.captureException(error);
    toast.error('Error fetching jobs.');
    console.error('Error fetching jobs:', error);
    throw error;
  }
};

export const getJobById = async ({ jobID }) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/jobs/get-opportunity/${jobID}`,
    );
    const data = await response.json();
    // Normalize the response to match the structure from other endpoints
    return {
      id: jobID, // Add the id property for consistency
      ...data,
    };
  } catch (error) {
    Sentry.captureException(error);
    toast.error('Error fetching job details.');
    console.error('Error fetching job by ID:', error);
    throw error;
  }
};

export const getCompany = async () => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/jobs/get_company`,
    );
    const data = await response.json();
    return data;
  } catch (error) {
    Sentry.captureException(error);
    toast.error('Error fetching company name.');
    console.error('Error fetching company name:', error);
    throw error;
  }
};

export const updateJobStage = async ({ uid, jobID, stage }) => {
  try {
    const response = await fetch(
        `${BACKEND_URL}/api/jobs/update-job-stage/${uid}/${jobID}/${stage}`,
      {
        method: 'PUT',
        headers: await getFirebaseAuthHeaders(undefined, {
          'Content-Type': 'application/json',
        }),
      },
    );

    if (response.ok) {
      toast.success('Job stage updated successfully');
      return { success: true };
    }
    toast.error('Failed to update job stage');
    return { success: false };
  } catch (error) {
    Sentry.captureException(error);
    toast.error('An error occurred while updating job stage');
    console.error('Error updating job stage:', error);
    return { success: false };
  }
};

export const fetchUserJobs = async ({ uid }) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/jobs/get-user-jobs/${uid}`,
      {
        headers: await getFirebaseAuthHeaders(),
      },
    );
    const data = await response.json();

    if (data.error) {
      console.error('Error fetching user jobs:', data.error);
      return {
        bookmarkedJobs: [],
        appliedJobs: [],
        jobStages: {
          applied: [],
          got_interview: [],
          got_offer: [],
          none: [],
          rejected: [],
        },
        allUserJobs: [],
      };
    }

    // Extract bookmarked job IDs
    const bookmarkedJobIds = data.bookmarked || [];

    // Extract applied job IDs
    const appliedJobIds = data.job_stages?.applied || [];

    // Convert jobs object to array format
    const allUserJobs = Object.keys(data.jobs || {}).map((key) => ({
      id: key,
      ...data.jobs[key],
    }));

    // Filter jobs to get bookmarked and applied job objects
    const bookmarkedJobs = allUserJobs.filter((job) => bookmarkedJobIds.includes(job.id));
    const appliedJobs = allUserJobs.filter((job) => appliedJobIds.includes(job.id));

    return {
      bookmarkedJobs,
      appliedJobs,
      jobStages: data.job_stages || {
        applied: [],
        got_interview: [],
        got_offer: [],
        none: [],
        rejected: [],
      },
      allUserJobs,
      bookmarkedJobIds,
    };
  } catch (error) {
    Sentry.captureException(error);
    toast.error('Error fetching user jobs.');
    console.error('Error fetching user jobs:', error);
    throw error;
  }
};
